import { Component, OnInit } from '@angular/core';
import { first } from 'rxjs/operators';
import { MessageService, ConfirmationService, LazyLoadEvent } from 'primeng/api';
import { DripCampaignInstance } from '@app/_models/drip-campaign';
import { DripCampaignService } from '../drip-campaign.service';
import { AlertService } from '@app/_services';
import * as moment from 'moment';

@Component({
  selector: 'app-drip-campaign-instance-list',
  templateUrl: './drip-campaign-instance-list.component.html',
  providers: [MessageService, ConfirmationService],
  standalone: false
})
export class DripCampaignInstanceListComponent implements OnInit {

  instances: DripCampaignInstance[] = [];
  recordTotal = 0;
  loading = true;
  lazyLoadEvent: LazyLoadEvent;
  multiSortMeta: any[] = [];

  selectedInstance: DripCampaignInstance = null;
  showDetail = false;
  cancelReason = '';
  showCancelDialog = false;
  cancelTarget: DripCampaignInstance = null;

  statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Paused', value: 'PAUSED' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cold', value: 'COLD' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];

  taskSourceOptions = [
    { label: 'Quote', value: 4 },
    { label: 'Owner Lead', value: 5 },
    { label: 'Owner Statement', value: 6 },
    { label: 'Onboarding', value: 7 },
    { label: 'Reservation', value: 2 }
  ];

  taskSourceLabels: { [key: number]: string } = {
    1: 'Note', 2: 'Reservation', 3: 'Guest',
    4: 'Quote', 5: 'Owner Lead', 6: 'Owner Statement', 7: 'Onboarding', 8: 'Msg', 9: 'Property'
  };

  taskSourceRoutes: { [key: number]: string } = {
    4: '/quotes',
    5: '/owner-lead-profile'
  };

  selectedStatuses: string[] = ['ACTIVE', 'PAUSED'];
  selectedTaskSources: number[] = [];
  dateFrom: Date = null;
  dateTo: Date = null;
  searchText = '';

  constructor(
    private svc: DripCampaignService,
    private alertService: AlertService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.multiSortMeta = [{ field: 'createDate', order: -1 }];
  }

  loadLazy(event: LazyLoadEvent) {
    try {
      this.loading = true;
      this.lazyLoadEvent = event;

      if (!event.multiSortMeta) {
        event.multiSortMeta = [{ field: 'createDate', order: -1 }];
      }

      const filters: any = { ...(event.filters || {}) };

      if (this.selectedStatuses && this.selectedStatuses.length > 0) {
        filters['status'] = { value: this.selectedStatuses, matchMode: 'in' };
      }
      if (this.selectedTaskSources && this.selectedTaskSources.length > 0) {
        filters['taskSource'] = { value: this.selectedTaskSources, matchMode: 'in' };
      }
      if (this.searchText && this.searchText.trim()) {
        filters['taskSourceId'] = { value: this.searchText.trim(), matchMode: 'contains' };
      }

      const page = Math.round((event.first || 0) / (event.rows || 50));
      this.svc.listInstances(page, event.rows || 50, event.sortField, event.sortOrder, event.multiSortMeta, event.globalFilter, filters)
        .pipe(first())
        .subscribe(
          (res: any) => {
            this.instances = res?.data?.dripCampaignInstances ?? [];
            this.recordTotal = res?.page?.recordTotal ?? 0;
          },
          (err: any) => { this.alertService.error(err); },
          () => { this.loading = false; }
        );
    } catch (error) {
      console.error('loadLazy error', error);
      this.loading = false;
    }
  }

  applyFilters() {
    if (this.lazyLoadEvent) {
      this.loadLazy({ ...this.lazyLoadEvent, first: 0 });
    }
  }

  onViewDetail(instance: DripCampaignInstance) {
    this.selectedInstance = instance;
    this.showDetail = true;
  }

  onDetailClosed() {
    this.showDetail = false;
    this.selectedInstance = null;
    if (this.lazyLoadEvent) { this.loadLazy(this.lazyLoadEvent); }
  }

  onPause(instance: DripCampaignInstance) {
    this.svc.pauseInstance(instance.dripCampaignInstanceId).pipe(first()).subscribe(
      () => {
        instance.status = 'PAUSED';
        this.messageService.add({ severity: 'success', summary: 'Paused', detail: 'Campaign paused.', life: 3000 });
      },
      (err: any) => this.alertService.error(err)
    );
  }

  onResume(instance: DripCampaignInstance) {
    this.svc.resumeInstance(instance.dripCampaignInstanceId).pipe(first()).subscribe(
      () => {
        instance.status = 'ACTIVE';
        this.messageService.add({ severity: 'success', summary: 'Resumed', detail: 'Campaign resumed.', life: 3000 });
      },
      (err: any) => this.alertService.error(err)
    );
  }

  onCancelPrompt(instance: DripCampaignInstance) {
    this.cancelTarget = instance;
    this.cancelReason = '';
    this.showCancelDialog = true;
  }

  confirmCancel() {
    if (!this.cancelTarget) { return; }
    this.svc.cancelInstance(this.cancelTarget.dripCampaignInstanceId, this.cancelReason).pipe(first()).subscribe(
      () => {
        this.cancelTarget.status = 'CANCELLED';
        this.showCancelDialog = false;
        this.cancelTarget = null;
        this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'Campaign cancelled.', life: 3000 });
      },
      (err: any) => { this.alertService.error(err); this.showCancelDialog = false; }
    );
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PAUSED': return 'warning';
      case 'COMPLETED': return 'info';
      case 'COLD': return 'secondary';
      case 'CANCELLED': return 'danger';
      default: return 'secondary';
    }
  }

  getEntityRoute(instance: DripCampaignInstance): string | null {
    const base = this.taskSourceRoutes[instance.taskSource];
    return base && instance.taskSourceId ? `${base}/${instance.taskSourceId}` : null;
  }

  getTaskSourceLabel(val: number): string {
    return this.taskSourceLabels[val] || ('Source ' + val);
  }

  getNextStepRelative(dateStr: string): string {
    if (!dateStr) { return '—'; }
    const m = moment(dateStr);
    const now = moment();
    const diff = m.diff(now, 'hours', true);
    if (diff < 0) {
      const hrs = Math.abs(Math.round(diff));
      return `overdue ${hrs}h`;
    }
    if (diff < 24) {
      return `in ${Math.round(diff)}h`;
    }
    return `in ${Math.round(diff / 24)}d`;
  }
}
